/**
 * API Core — base URL, token storage, fetch wrapper, offline fallback.
 * Used by all domain modules in ./api/*.js
 */
import { syncManager } from '../services/SyncManager'
import { ONBOARDIX_TOKEN_KEY as ONBOARDIX_TOKEN_KEY_INTERNAL, ONBOARDIX_USER_KEY } from '../utils/storageKeys'
import { ABORT_REASON, abortReason } from '../utils/abortError'

const explicitApiBase = (import.meta.env.VITE_API_BASE || '').trim()
const isLocalDevHost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)

// Last-resort fallback when neither VITE_API_BASE nor local-dev heuristic applies.
// main.jsx consumes API_BASE instead — one source of truth through the resolved constant.
const PROD_API_FALLBACK = 'https://onboardix-backend-production.up.railway.app/api/v1'

const defaultApiBase = import.meta.env.DEV && isLocalDevHost
    ? 'http://localhost:8000/api/v1'
    : PROD_API_FALLBACK

export const API_BASE = explicitApiBase || defaultApiBase

// Surface config gap: if running in a non-local-dev bundle without VITE_API_BASE,
// the baked-in Railway URL is masking a missing build-time env var. Shout about it
// so deploys with misconfigured envs aren't silently locked to the default backend.
if (!explicitApiBase && !(import.meta.env.DEV && isLocalDevHost)) {
    // eslint-disable-next-line no-console
    console.warn(
        '[core.js] VITE_API_BASE is not set — falling back to %s. ' +
        'Set VITE_API_BASE at build time to silence this warning.',
        PROD_API_FALLBACK,
    )
}

/** Absolute URL for paths returned by the API (e.g. `/api/v1/documents/file/...`). */
export function resolveApiAssetUrl(relativePath) {
    if (!relativePath) return ''
    if (/^https?:\/\//i.test(relativePath)) return relativePath
    const root = API_BASE.replace(/\/api\/v1\/?$/, '')
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    return `${root}${path}`
}

export { ONBOARDIX_TOKEN_KEY } from '../utils/storageKeys'

const TOKEN_KEY = ONBOARDIX_TOKEN_KEY_INTERNAL
const USER_KEY = ONBOARDIX_USER_KEY

/** Same-tab listeners (e.g. Wiki image hydration) — storage event fires only across tabs. */
function notifyOnboardixTokenChanged() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('onboardix:token-changed'))
}

export function getToken() {
    return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token) {
    const prev = localStorage.getItem(TOKEN_KEY)
    localStorage.setItem(TOKEN_KEY, token)
    if (prev !== token) notifyOnboardixTokenChanged()
}

export function removeToken() {
    const had = !!localStorage.getItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    if (had) notifyOnboardixTokenChanged()
}

export function getCachedUser() {
    try {
        return JSON.parse(localStorage.getItem(USER_KEY))
    } catch {
        return null
    }
}

export function setCachedUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
}

// AI-heavy routes → 180s client timeout (HARD GATE 6). Exported + single-sourced
// so it stays testable; every new Gemini-backed endpoint must be added here or
// the default 30s aborts before the backend's Gemini budget → phantom
// "Сервер недоступен. Работаем в офлайн-режиме".
// dialogues/* добавлены 2026-06-16 (Диалог-тренажёр генерит ход AI через Gemini).
export const AI_TIMEOUT_ROUTE_RX =
    /\/(interview-questions|ai-match|parse-resume|ai-studio|knowledge|import-file|import-text|job-fit|sessions\/synthesis|profile-synthesis|regenerate-interpretations|ideal-profile\/generate|job-profiles\/generate|ai-recommendations|vacancies\/parse-text|support\/chat|operations\/suggest|dialogues\/(start|preflight|generate-from-real|real\/replay|[^/]+\/(message|hint|evaluate|turn-feedback)|scenarios\/[^/]+\/generate-ai-config))/

export function isAiTimeoutRoute(path) {
    return AI_TIMEOUT_ROUTE_RX.test(path)
}

/** Публичные ручки записи гостя — им нельзя в офлайн-очередь (#6521). */
export function isGuestBookingRoute(path) {
    return /^\/public\/meet(\/|\?|$)/.test(String(path || ''))
}

// Ручки входа без сессии (#5618). Bearer к ним не прикладывается: сервер ставит
// контекст организации по токену, и устаревший токен организации A прячет от
// входа пользователя B. Метод входит в ключ: GET-список заявок на сброс пароля и
// их закрытие — админские ручки и токен получают как обычно.
const PUBLIC_AUTH_ROUTES = new Set([
    'POST /auth/login',
    'POST /password-reset-requests',
    'GET /invite/peek',
    'POST /invite/redeem',
    // Вход по ключу доступа и через Telegram (#5618): учётку они ищут так же,
    // как пароль, и под политикой на `users` чужой контекст её прячет.
    'POST /webauthn/login/begin',
    'POST /webauthn/login/finish',
    'POST /auth/telegram/bot/poll',
    'POST /auth/telegram/oidc/callback',
    'POST /auth/telegram-webapp',
])

/** Ручка входа без сессии: к ней не едет Bearer и её нет в офлайн-очереди. */
export function isPublicAuthRoute(path, method = 'GET') {
    let bare = String(path || '').split('?')[0]
    if (bare.length > 1 && bare.endsWith('/')) bare = bare.slice(0, -1)
    return PUBLIC_AUTH_ROUTES.has(`${String(method).toUpperCase()} ${bare}`)
}

/** Машинный код из тела отказа: плоский `code` или `detail.code`. */
function errorCodeOf(data) {
    if (typeof data?.code === 'string' && data.code) return data.code
    const detail = data?.detail
    if (detail && typeof detail === 'object' && !Array.isArray(detail)
        && typeof detail.code === 'string' && detail.code) return detail.code
    return undefined
}

/**
 * User-facing message for a network / timeout failure.
 *
 * HARD RULE: production users must NEVER see infrastructure debugging text
 * (VPN / firewall / CORS_ORIGINS / VITE_API_BASE / "вкладка Network" /
 * "офлайн-режим"). That leaked the engineer-only hint into a scary wall and is
 * exactly what end users complained about. In prod we say only what the user can
 * act on: their work is saved and will sync automatically. The full diagnostic
 * is kept for DEV builds only (engineers debugging CORS / env / cold-start).
 *
 * Control flow elsewhere (Sentry filtering, local-save fallbacks) keys off the
 * structured `err.isOffline` / `err.offlineReplayed` flags, NOT this string —
 * so the wording can stay purely human without breaking offline persistence.
 *
 * @param {object}  o
 * @param {boolean} o.isTimeout   AbortError (timeout) vs network/CORS failure
 * @param {boolean} o.isFormData  multipart upload (cannot be replayed from queue)
 * @param {boolean} o.isReplayed  request was enqueued for offline replay (saved)
 * @param {boolean} o.isDev       import.meta.env.DEV
 * @param {string}  [o.devUrl]    request URL, appended in DEV only
 * @returns {string}
 */
export function buildOfflineMessage({ isTimeout, isFormData, isReplayed, isDev, devUrl }) {
    if (!isDev) {
        // Production — calm, reassuring, no infra jargon, no "офлайн-режим".
        if (isReplayed) {
            return 'Изменения сохранены и отправятся автоматически, как только восстановится связь с сервером.'
        }
        if (isFormData) {
            return 'Не удалось отправить файл — связь с сервером прервалась. Повторите, когда соединение восстановится.'
        }
        if (isTimeout) {
            return 'Сервер отвечает дольше обычного. Подождите минуту и повторите — введённые данные не потеряны.'
        }
        return 'Нет связи с сервером. Проверьте интернет-соединение и повторите — ваша работа сохранена.'
    }
    // DEV — keep the full diagnostic for engineers.
    const detail = isTimeout
        ? 'Превышено время ожидания ответа (таймаут). Часто на cold start Railway — повторите через минуту.'
            + (isFormData ? ' Большие PDF на медленном канале могут не уложиться в ожидание — повторите или загрузите с более стабильной сети.' : '')
        : 'Нет сетевого ответа от API. Проверьте VPN/брандмауэр, блокировщики, что VITE_API_BASE указывает на живой бэкенд и в Railway в CORS_ORIGINS указан точный origin вашего ERP (вкладка Network).'
            + (isFormData ? ' Для multipart в DevTools смотрите статус запроса (failed / CORS / blocked).' : '')
    return `Сервер недоступен. Работаем в офлайн-режиме. ${detail}${devUrl ? ` Запрос: ${devUrl}` : ''}`
}

// ── Тихий перевход (Telegram Mini App) ───────────────────────────────────────
// Внутри Telegram у сотрудника нет формы входа: вход идёт подписью Telegram
// (initData). Поэтому 401 там значит не «войдите снова», а «токен протух —
// перевойдите сами». TmaContext регистрирует здесь функцию перевхода; вне Mini
// App её нет, и 401 ведёт себя как раньше — выкидывает на логин.
let _unauthorizedRecovery = null
let _recoveryInFlight = null

/**
 * Зарегистрировать тихий перевход на 401. Функция возвращает `true`, если
 * положила свежий токен. Возвращает функцию снятия регистрации.
 */
export function setUnauthorizedRecovery(fn) {
    _unauthorizedRecovery = fn
    return () => {
        if (_unauthorizedRecovery === fn) _unauthorizedRecovery = null
    }
}

// Пачка параллельных 401 ждёт ОДИН общий перевход, а не шлёт свой каждый.
function _recoverOnce() {
    if (!_recoveryInFlight) {
        const recover = _unauthorizedRecovery
        _recoveryInFlight = Promise.resolve()
            .then(() => recover())
            .then((ok) => ok === true, () => false)
            .finally(() => { _recoveryInFlight = null })
    }
    return _recoveryInFlight
}

// Вход подписью Telegram сам отвечает 401 на плохую подпись — это отказ входа,
// а не протухшая сессия: ни перевходить, ни выкидывать на логин нельзя.
const TMA_LOGIN_PATH = '/auth/telegram-webapp'

async function _fetchWithPolicy(path, options = {}, responseHandler = (r) => r.json()) {
    const method = (options.method || 'GET').toUpperCase()
    const isPublicAuth = isPublicAuthRoute(path, method)
    const token = isPublicAuth ? null : getToken()
    const isFormData = options.body instanceof FormData
    const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
    }

    let normalizedPath = path

    // AI-heavy endpoints need longer timeout (Gemini can take 15-30s).
    // HARD GATE 6: every new AI endpoint must land here, otherwise the
    // default 30s timeout aborts before backend's ~110s Gemini retry budget.
    // AI-heavy routes get 180s (see AI_TIMEOUT_ROUTE_RX above; HARD GATE 6).
    // `options.aiTimeout === true` — explicit opt-in for routes whose AI work is
    // body-conditional and thus invisible to the path regex (e.g. POST /candidates/
    // with resume_text triggers a synchronous Gemini parse, but the bare path is
    // shared with the fast GET list — see candidates.create). Без этого реальное
    // резюме (~40s парсинг) обрывалось на 30s → ложный «Сервер недоступен».
    const isAiRoute = options.aiTimeout === true || isAiTimeoutRoute(path)
    // Heavy exports: aggregate over большой команды → больше времени.
    const isHeavyExport = /\/learning\/analytics\/(team\/export\.csv|employee\/[^/]+\/(export\.csv|one-on-one\.html))/.test(path)
    // Wiki test submission may trigger AI grading (short_answer via Gemini)
    const isWikiTest = /\/wiki\/[^/]+\/test(\/|$)/.test(path)
    // Все запросы к /protocols (список + drive + upload): дольше таймаут — cold start / сеть.
    const isProtocolScope = /\/candidates\/[^/]+\/protocols(\/|$)/.test(path)
    const isSlowRoute = /\/(requisitions|candidates\/\?|candidates\/\?per_page)/.test(path) || (path.startsWith('/candidates/') && path.includes('per_page'))
    // Песочница: расчёт по сделкам дёргает P&L биржи, у которого statement
    // timeout 60s. С дефолтными 30s клиент рвал соединение раньше сервера,
    // человек видел «Сервер недоступен» и жал «Посчитать» второй раз.
    const isSandboxSimulate = path.startsWith('/sandbox/experiments/') && path.endsWith('/simulate')
    // Опрос кабинетов рекламного подключения: сервер ходит к провайдеру
    // синхронно, а у агентского аккаунта в ответе сотни клиентских кабинетов.
    // С дефолтными 30s клиент рвал бы соединение раньше сервера, человек видел
    // ложный отказ и жал кнопку второй раз — а каждый опрос жжёт квоту площадки.
    const isConnectorDiscovery = /^\/connectors\/[^/]+\/assets\/refresh$/.test(path)
    const isChatScope = path.startsWith('/chat/')
    const isChatPath = normalizedPath.startsWith('/chat/')
    const isProtocolPath = /\/candidates\/[^/]+\/protocols(\/|$)/.test(normalizedPath)
    // Только мутации: отдельное сообщение об ошибке и очистка sync queue.
    const isProtocolMutationRoute = /\/candidates\/[^/]+\/protocols\/(drive|upload)/.test(path)
    // Публичное подписание оффера: слэш в конце ломает FastAPI {token}
    const isOfferSigningPath = normalizedPath.startsWith('/offers/signing/')
    // Публичный портал тестов: избегаем 307 redirect на некоторых мобильных/корп-сетях.
    const isTestPortalPath = normalizedPath.startsWith('/tests/portal/')
    // Скачивание файлов по UUID: redirect_slashes=False → /files/{uuid}/ даёт 404.
    const isFileBlobPath = /^\/files\/[^/]+$/.test(normalizedPath)

    // Normalize trailing slash to avoid FastAPI 307 redirects.
    // For mutating requests (PUT/POST/PATCH/DELETE) we STRIP the trailing slash
    // because 307 redirect loses the request body in most browsers.
    // For GET requests we ADD the trailing slash (FastAPI expects it on some routes).
    const isMutating = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS'
    if (!isChatPath && !isProtocolPath && !isOfferSigningPath && !isTestPortalPath && !isAiRoute && !isFileBlobPath && !normalizedPath.includes('/reorder')) {
        if (isMutating) {
            // Strip trailing slash for mutating requests to prevent 307 body loss
            if (normalizedPath.endsWith('/') && normalizedPath.length > 1) {
                normalizedPath = normalizedPath.slice(0, -1)
            }
        } else {
            if (!normalizedPath.includes('?') && !normalizedPath.includes('.') && !normalizedPath.endsWith('/')) {
                normalizedPath += '/'
            } else if (normalizedPath.includes('?') && !normalizedPath.split('?')[0].endsWith('/')) {
                const [base, query] = normalizedPath.split('?')
                normalizedPath = `${base}/?${query}`
            }
        }
    }

    const url = `${API_BASE}${normalizedPath}`

    // FormData раньше всегда получал 30s и перекрывал isAiRoute — большие PDF в AI-Studio
    // обрывались по таймауту с тем же UX, что и «нет сети» на медленном канале.
    // Default 30s covers Railway cold-start (~15-25s observed). AI and upload
    // routes get longer budgets. No route should be below 30s — older 10s
    // default produced phantom "Сервер недоступен" messages after idle periods.
    // Retry path uses 60s baseline to survive a truly cold container.
    const attempt = options._attempt || 0
    const extendedRetry = !!options._extendedTimeout
    // AI routes: backend budget is ~110s for Gemini retries + up to 2 JSON-shape
    // retries on top → worst case ~220s on profile synthesis. Give client 180s
    // so we rarely abort before the server has a final answer.
    const baseTimeoutMs = isProtocolScope
        ? 60000
        : isAiRoute && isFormData
          ? 180000
          : isFormData
            ? 60000
            : isAiRoute || isWikiTest
              ? 180000
              : isHeavyExport || isSandboxSimulate || isConnectorDiscovery
                ? 60000
                : isSlowRoute || isChatScope
                  ? 45000
                  : 30000
    const timeoutMs = extendedRetry ? Math.max(baseTimeoutMs, 60000) : baseTimeoutMs
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(abortReason(ABORT_REASON.timeout)), timeoutMs)

    // Убираем старые офлайн-ретраи по протоколам, чтобы они не создавали
    // бесконечный шум и повторные невалидные попытки после фиксов CORS/сети.
    if (isProtocolMutationRoute) {
        syncManager.removeWhere(op => {
            const u = String(op?.url || '')
            return u.includes('/protocols/drive') || u.includes('/protocols/upload')
        })
    }

    try {
        const response = await fetch(url, {
            ...options,
            headers,
            signal: options.signal || controller.signal,
        })

        clearTimeout(timeoutId)

        if (response.status === 401) {
            if (path.includes('/auth/login')) {
                const data = await response.json().catch(() => ({}))
                throw new Error(data.detail || 'Неверный логин или пароль')
            }
            if (path.includes(TMA_LOGIN_PATH)) {
                const data = await response.json().catch(() => ({}))
                const loginErr = new Error(data.detail || 'invalid_init_data')
                loginErr.status = 401
                loginErr.detail = data.detail
                throw loginErr
            }
            if (!options._reauthAttempted) {
                // Токен сменился, пока запрос летел (соседний запрос уже
                // перевошёл) — просто повторить с новым.
                const current = getToken()
                if (_unauthorizedRecovery && current && current !== token) {
                    return _fetchWithPolicy(path, { ...options, _reauthAttempted: true }, responseHandler)
                }
                if (_unauthorizedRecovery && await _recoverOnce()) {
                    return _fetchWithPolicy(path, { ...options, _reauthAttempted: true }, responseHandler)
                }
            }
            removeToken()
            window.dispatchEvent(new Event('auth:logout'))
            throw new Error('Сессия истекла. Войдите снова.')
        }

        if (response.status === 204) return null

        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            const detail = data.detail
            let message
            if (Array.isArray(detail)) {
                message = detail.map(e => e.msg || JSON.stringify(e)).join('; ')
            } else if (detail && typeof detail === 'object') {
                // Structured error payload (e.g. cooldown:
                // { error, message, retry_after_sec }). Render a human field
                // for `.message` — never the raw object (that printed as the
                // infamous "[object Object]" in alert UIs). The original object
                // stays on `err.detail` so callers can branch on its fields.
                message = detail.message || detail.msg || detail.error
            } else {
                message = detail
            }
            const err = new Error(message || `Ошибка сервера (${response.status})`)
            // Expose status + structured detail so callers can branch
            // (e.g. 429 cooldown countdown). Previously both were dropped,
            // making every structured-error handler downstream dead code.
            err.status = response.status
            err.detail = data.detail
            // Машинный код отказа (`slot_taken`, `booking_not_changeable`, …).
            // Ручки с плоским контрактом `{detail: текст, code}` кладут его рядом
            // с detail — раньше клиент его терял, и страница не могла отличить
            // два разных 409 без повторного чтения. Структурный detail
            // `{code, message}` даёт тот же код. Поле только добавляется:
            // message/status/detail остаются прежними.
            const code = errorCodeOf(data)
            if (code) err.code = code
            throw err
        }

        return await responseHandler(response)
    } catch (err) {
        clearTimeout(timeoutId)
        const isOfflineErr = err.name === 'AbortError'
            || (err.name === 'TypeError' && err.message.includes('fetch'))

        if (isOfflineErr) {
            // Auto-retry once for idempotent reads on the first timeout.
            // Railway cold-start routinely exceeds the 30s default; a single
            // silent retry with a longer budget masks that without showing
            // an "offline" banner that confuses users.
            const isIdempotent = method === 'GET' || method === 'HEAD' || method === 'OPTIONS'
            if (isIdempotent && err.name === 'AbortError' && attempt === 0) {
                console.info('[API] Timeout on first attempt — retrying once with extended budget:', url)
                return _fetchWithPolicy(path, { ...options, _attempt: 1, _extendedTimeout: true }, responseHandler)
            }
            console.warn('[API] Offline/timeout:', url)

            // ── Auto-enqueue mutating requests for replay when back online ──
            // GET requests are not enqueued — they'll be re-fetched on reconnect.
            // FormData нельзя корректно переиграть из очереди (SyncManager шлёт JSON).
            // Ручки входа без сессии не реплеим: ответ всё равно отбрасывается,
            // тело с логином/паролем не должно оседать в localStorage-очереди, а
            // при отправке из очереди к нему приложился бы актуальный токен.
            const isAuthLogin = path.includes('/auth/login') || isPublicAuth
            // Опрос кабинетов — POST по форме, но по сути чтение: он ничего не
            // сохраняет, зато КАЖДЫЙ вызов тратит квоту провайдера. Переигранный
            // из очереди опрос сжёг бы её второй раз и соврал бы человеку
            // «Изменения сохранены и отправятся автоматически» — сохранять там
            // нечего. Повторяет его человек кнопкой, когда связь вернулась.
            // Запись гостя (`/public/meet/...`: записаться, перенести, отменить)
            // не откладывается никогда. Гость без входа уже ушёл со страницы,
            // когда очередь проиграла бы запрос через часы: встреча возникла бы
            // на время, которое он давно не ждёт, а сам он видел «изменения
            // сохранены». Ему честнее увидеть отказ и нажать кнопку ещё раз —
            // повтор сервер узнает по Idempotency-Key.
            const isGuestBooking = isGuestBookingRoute(path)
            const shouldReplay = method !== 'GET' && !isProtocolMutationRoute && !isFormData
                && !isAuthLogin && !isConnectorDiscovery && !isGuestBooking
            if (shouldReplay) {
                const token = getToken()
                // Ключ повторной отправки обязан ПЕРЕЖИТЬ обрыв: сервер отличает
                // повтор от нового действия только по нему. Раньше очередь
                // хранила лишь url/method/body, заголовок терялся, и когда связь
                // возвращалась, сервер видел обычный POST и заводил второе дело.
                const idempotencyKey = headers['Idempotency-Key'] || null
                // Операция очереди опознаётся ЛОГИЧЕСКИМ действием, а не моментом
                // неудачи. С `Date.now()` в ключе каждое повторное нажатие
                // ложилось в очередь отдельной записью, и после восстановления
                // связи в CRM появлялось столько одинаковых дел, сколько раз
                // человек нажал кнопку. Без ключа идемпотентности склеивать
                // нельзя — два разных дела могут отличаться только телом.
                const dedupeKey = idempotencyKey ? `${method} ${url} idem:${idempotencyKey}` : null
                syncManager.enqueue({
                    key: dedupeKey || `${method}-${url}-${Date.now()}`,
                    // Кто именно завёл это действие. Очередь переживает выход и
                    // вход другого человека на той же машине; без метки автора
                    // отложенное дело оформилось бы на того, кто вошёл позже, —
                    // а авторство дела в партнёрке это KPI менеджера.
                    userId: getCachedUser()?.id ?? null,
                    dedupeKey,
                    type: 'http-replay',
                    url,
                    method,
                    body: options.body || null,         // already serialized string
                    // Только ключ повторной отправки: Authorization едет отдельным
                    // полем и при отправке из очереди подменяется актуальным
                    // токеном, а остальным заголовкам нечего лежать сутки в
                    // localStorage.
                    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
                    authHeader: token ? `Bearer ${token}` : null,
                    timestamp: Date.now(),
                })
                console.info(`[API] Enqueued for offline replay: ${method} ${url}`)
            }

            const isTimeout = err.name === 'AbortError'

            if (isProtocolMutationRoute) {
                const hint = isTimeout ? ' Превышено время ожидания ответа.' : ''
                const protoErr = new Error(
                    'Не удалось прикрепить протокол. Проверьте доступ к серверу, отключите блокировщик рекламы для этого сайта и повторите.'
                    + hint,
                )
                protoErr.isOffline = true
                protoErr.offlineReplayed = false
                protoErr.offlineTimeout = isTimeout
                throw protoErr
            }

            // Structured flags drive control flow (Sentry filter, local-save
            // fallbacks). The message is purely human — no infra jargon in prod.
            const offlineErr = new Error(buildOfflineMessage({
                isTimeout,
                isFormData,
                isReplayed: shouldReplay,
                isDev: import.meta.env.DEV,
                devUrl: url,
            }))
            offlineErr.isOffline = true
            offlineErr.offlineReplayed = shouldReplay
            offlineErr.offlineTimeout = isTimeout
            throw offlineErr
        }
        throw err
    }
}

/** Default API call: returns parsed JSON. 204 → null. !ok → throws with detail. */
export async function apiFetch(path, options = {}) {
    return _fetchWithPolicy(path, options)
}

/**
 * Same auth/timeout/retry/offline policy as `apiFetch`, but returns the raw
 * Response so callers can read `.blob()`, `.text()`, or stream large downloads
 * (CSV/HTML exports, protected images). Use this instead of bare `fetch()` —
 * bare fetch bypasses HARD GATE 6 cold-start tolerance (30s+ timeout + retry).
 * 401 still triggers logout. !ok still throws.
 */
export async function apiFetchRaw(path, options = {}) {
    return _fetchWithPolicy(path, options, (r) => r)
}

export async function apiHealth() {
    // 15с — чтобы переждать Railway cold-start (~15-25с) без ложного "Сервер недоступен".
    // Ограничиваем сверху, чтобы SW, застрявший на запросе, не держал health-пинг вечно.
    const controller = new AbortController()
    const t = setTimeout(() => controller.abort(), 15000)
    try {
        const resp = await fetch(`${API_BASE.replace('/api/v1', '')}/health`, {
            signal: controller.signal,
            cache: 'no-store',
        })
        return await resp.json()
    } catch {
        return null
    } finally {
        clearTimeout(t)
    }
}

const CACHE_PREFIX = 'onboardix_cache_'

export async function fetchWithFallback(apiCall, cacheKey, _fallbackData) {
    try {
        const data = await apiCall()
        localStorage.setItem(`${CACHE_PREFIX}${cacheKey}`, JSON.stringify(data))
        return { data, source: 'api' }
    } catch (err) {
        // Причина отказа возвращается вместе с данными: без неё страница не
        // отличает «нет связи» от «нет прав» и на 403 предлагает «Повторить»,
        // хотя повторять нечего. Поле добавлено, ничего не переименовано —
        // старые вызовы читают `data`/`source` как читали.
        try {
            const cached = localStorage.getItem(`${CACHE_PREFIX}${cacheKey}`)
            if (cached) return { data: JSON.parse(cached), source: 'cache', error: err }
        } catch { /* ignore */ }
        return { data: null, source: 'offline', error: err }
    }
}
